import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ArrowLeft, Search, AlertCircle, Download, Printer, Eye, AlertTriangle, Edit2,
  MessageSquare, Phone, Check
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { generateManagementFormPDF } from '../utils/generateManagementFormPDF';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const ProblemsEmergenciesLibraryPage = () => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProblemId, setSelectedProblemId] = useState(null);
  const [viewingProblem, setViewingProblem] = useState(null);
  const [editingProblem, setEditingProblem] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', category: '', overview: '' });
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { practice } = useAuth();

  // SMS state
  const [patients, setPatients] = useState([]);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsProblem, setSmsProblem] = useState(null);
  const [smsPatientSearchQuery, setSmsPatientSearchQuery] = useState('');
  const [filteredSmsPatients, setFilteredSmsPatients] = useState([]);
  const [smsPatient, setSmsPatient] = useState(null);
  const [sendingSMS, setSendingSMS] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const categories = ['All', 'General', 'Surgical', 'Restorative', 'Periodontics', 'Orthodontics', 'Prosthodontics', 'Endodontics', 'Cosmetic'];

  useEffect(() => {
    loadProblems();
    loadPatients();
  }, []);

  // Filter patients for SMS modal - only show patients with phone numbers
  useEffect(() => {
    let filtered = [...patients];
    filtered = filtered.filter(patient => patient.cellPhone || patient.phone);

    if (smsPatientSearchQuery.trim()) {
      const searchLower = smsPatientSearchQuery.toLowerCase();
      filtered = filtered.filter(patient =>
        `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchLower) ||
        (patient.email && patient.email.toLowerCase().includes(searchLower))
      );
    }

    filtered.sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`;
      const nameB = `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB);
    });

    setFilteredSmsPatients(filtered);
  }, [smsPatientSearchQuery, patients]);

  const loadPatients = async () => {
    try {
      const token = localStorage.getItem('dentalToken');
      const response = await axios.get(
        `${backendUrl}/api/practice/patients`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPatients(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load patients:', error);
    }
  };

  const loadProblems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dentalToken');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/problems-emergencies/library`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setProblems(response.data.data || []);
    } catch (error) {
      console.error('Failed to load problems/emergencies:', error);
      toast({
        title: "Error",
        description: "Failed to load problems and emergencies",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredProblems = problems.filter(problem => {
    const matchesSearch = problem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         problem.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || problem.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleView = (problem) => {
    setViewingProblem(problem);
  };

  const handleDownload = async (problem) => {
    // Use same PDF generator as management forms
    const formattedProblem = {
      title: problem.name,
      category: problem.category,
      overview: problem.overview
    };
    
    try {
      let practiceData = practice;
      if (!practice || !practice.name) {
        try {
          const practiceApi = (await import('../services/authApi')).practiceApi;
          const dashboardData = await practiceApi.getDashboard();
          if (dashboardData.success && dashboardData.data?.practice) {
            practiceData = dashboardData.data.practice;
          }
        } catch (error) {
          console.error('Failed to fetch practice data:', error);
        }
      }
      
      const success = await generateManagementFormPDF(formattedProblem, practiceData);
      
      if (success) {
        toast({
          title: "Downloaded",
          description: "Problem/Emergency guide downloaded successfully",
          variant: "default",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download",
        variant: "destructive",
      });
    }
  };

  const handlePrint = (problem) => {
    const printWindow = window.open('', '', 'height=800,width=800');
    const practiceName = practice?.name || 'Dental Practice';
    
    let practiceAddress = '';
    if (practice?.address) {
      if (typeof practice.address === 'object') {
        const addr = practice.address;
        practiceAddress = `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || ''}`.trim();
      } else {
        practiceAddress = practice.address;
      }
    }
    
    const practicePhone = practice?.phone || '';
    const practiceEmail = practice?.email || '';
    const practiceLogo = practice?.branding?.logo || '';
    
    printWindow.document.write('<html><head><title>' + problem.name + '</title>');
    printWindow.document.write('<style>');
    printWindow.document.write('@page { margin: 0.75in; }');
    printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; margin: 0; }');
    printWindow.document.write('.header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; page-break-after: avoid; break-after: avoid; }');
    printWindow.document.write('.logo { max-width: 150px; max-height: 80px; margin: 0 auto 15px; display: block; }');
    printWindow.document.write('.practice-name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }');
    printWindow.document.write('.practice-info { font-size: 12px; color: #666; }');
    printWindow.document.write('.form-title { font-size: 20px; font-weight: bold; margin: 20px 0 10px 0; }');
    printWindow.document.write('h2, h3, h4 { page-break-after: avoid; break-after: avoid; page-break-inside: avoid; break-inside: avoid; orphans: 4; widows: 4; margin-top: 18px; margin-bottom: 10px; }');
    printWindow.document.write('p { orphans: 4; widows: 4; page-break-inside: avoid; break-inside: avoid; margin-bottom: 10px; line-height: 1.6; }');
    printWindow.document.write('.bullet-item { margin-left: 24px; margin-bottom: 6px; padding-left: 20px; text-indent: -20px; page-break-inside: avoid; break-inside: avoid; line-height: 1.5; }');
    printWindow.document.write('pre { white-space: pre-wrap; font-family: Arial; margin-bottom: 60px; page-break-inside: avoid; break-inside: avoid; }');
    printWindow.document.write('.footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; font-size: 10px; color: #666; page-break-inside: avoid; break-inside: avoid; }');
    printWindow.document.write('.footer-title { font-weight: bold; font-size: 12px; margin-bottom: 5px; }');
    printWindow.document.write('</style>');
    printWindow.document.write('</head><body>');
    
    // Header
    printWindow.document.write('<div class="header">');
    if (practiceLogo) {
      const logoSrc = practiceLogo.startsWith('data:image') ? practiceLogo : `data:image/png;base64,${practiceLogo}`;
      printWindow.document.write('<img src="' + logoSrc + '" class="logo" alt="Practice Logo" />');
    }
    printWindow.document.write('<div class="practice-name">' + practiceName + '</div>');
    if (practiceAddress) printWindow.document.write('<div class="practice-info">' + practiceAddress + '</div>');
    if (practicePhone) printWindow.document.write('<div class="practice-info">Phone: ' + practicePhone + '</div>');
    if (practiceEmail) printWindow.document.write('<div class="practice-info">Email: ' + practiceEmail + '</div>');
    printWindow.document.write('</div>');
    
    // Use EXACT SAME rendering logic as VIEW - merge paragraphs properly
    const rawLines = (problem.overview || '').split('\n');
    printWindow.document.write('<div style="margin-top: 20px; color: #374151; line-height: 1.625;">');
    
    let isFirstLines = true;
    let i = 0;
    
    while (i < rawLines.length) {
      const line = rawLines[i];
      const trimmed = line.trim();
      
      // Skip empty lines
      if (trimmed === '') {
        printWindow.document.write('<div style="margin-bottom: 8px;"></div>');
        i++;
        continue;
      }
      
      // First 2 non-empty lines are the title - larger and centered
      if (isFirstLines && !trimmed.startsWith('This handout')) {
        const titleCount = printWindow.document.querySelector ? 0 : isFirstLines ? 0 : 999;
        if (titleCount < 2) {
          printWindow.document.write('<h2 style="font-weight: bold; color: #111827; font-size: 24px; text-align: center; margin-bottom: 8px;">' + trimmed + '</h2>');
          i++;
          if (titleCount >= 1) isFirstLines = false;
          continue;
        }
      }
      
      isFirstLines = false;
      
      // Numbered items
      if (trimmed.match(/^\d+\.\s+/)) {
        printWindow.document.write('<h4 style="font-weight: bold; color: #111827; margin-top: 16px; margin-bottom: 8px;">' + trimmed + '</h4>');
        i++;
        continue;
      }
      
      // Section headings
      const isMainHeading = 
        trimmed.match(/^Common Problems \(.*Emergency\)/i) ||
        trimmed.match(/^When to Call the Dentist/i) ||
        trimmed.match(/^Warning Signs & When to Call/i) ||
        trimmed.match(/^What You May Notice/i);
      
      if (isMainHeading) {
        printWindow.document.write('<h3 style="font-weight: bold; color: #111827; margin-top: 24px; margin-bottom: 12px; font-size: 18px;">' + trimmed + '</h3>');
        i++;
        continue;
      }
      
      // Bullet points (indented)
      if (line.startsWith(' ') || line.startsWith('\t')) {
        const bulletText = trimmed.replace(/^[•-]\s*/, '');
        printWindow.document.write('<div class="bullet-item">• ' + bulletText + '</div>');
        i++;
        continue;
      }
      
      // Regular paragraph - MERGE consecutive lines just like VIEW
      let paragraph = trimmed;
      let j = i + 1;
      while (j < rawLines.length) {
        const nextLine = rawLines[j];
        const nextTrimmed = nextLine.trim();
        
        // Stop if empty, numbered, heading, or indented
        if (nextTrimmed === '' || 
            nextTrimmed.match(/^\d+\./) || 
            nextLine.startsWith(' ') ||
            nextLine.startsWith('\t') ||
            nextTrimmed.match(/^(Common Problems|When to Call|Warning Signs|What You May)/i)) {
          break;
        }
        
        paragraph += ' ' + nextTrimmed;
        j++;
      }
      
      printWindow.document.write('<p style="margin-bottom: 8px; line-height: 1.5; color: #374151;">' + paragraph + '</p>');
      i = j;
    }
    
    printWindow.document.write('</div>');
    
    // Footer
    printWindow.document.write('<div class="footer">');
    printWindow.document.write('<div class="footer-title">PractCom</div>');
    printWindow.document.write('<div>© ' + new Date().getFullYear() + ' The Oncall Bot LLC. All rights reserved.</div>');
    printWindow.document.write('<div>Powered by PractCom</div>');
    printWindow.document.write('</div>');
    
    printWindow.document.write('</body></html>');
    printWindow.document.close();

    // Chrome no longer reliably fires onload from a document.write'd script.
    const triggerPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => { try { printWindow.close(); } catch {} }, 500);
      } catch (e) {
        console.error('Print trigger error:', e);
      }
    };
    // Single guarded trigger. 300ms delay lets Chrome parse the inline
    // <style> before print() fires; guard prevents the ghost-window flash.
    let _printed = false;
    const guardedPrint = () => { if (_printed) return; _printed = true; triggerPrint(); };
    setTimeout(guardedPrint, 300);
  };

  // SMS Functions
  const handleTextProblem = (problem) => {
    setSmsProblem(problem);
    setSmsPatient(null);
    setSmsPatientSearchQuery('');
    setShowSmsModal(true);
  };

  const sendSMS = async () => {
    if (!smsPatient) {
      toast({
        title: "Patient Required",
        description: "Please select a patient to send the text",
        variant: "destructive",
      });
      return;
    }

    const phoneNumber = smsPatient.cellPhone || smsPatient.phone;
    if (!phoneNumber) {
      toast({
        title: "No Phone Number",
        description: "Selected patient does not have a phone number",
        variant: "destructive",
      });
      return;
    }

    try {
      setSendingSMS(true);
      const token = localStorage.getItem('dentalToken');
      const patientName = `${smsPatient.firstName} ${smsPatient.lastName}`;
      
      const response = await fetch(`${backendUrl}/api/sms/send-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: smsPatient.id,
          document_type: 'problem',
          document_id: smsProblem.id,
          phone_number: phoneNumber
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Failed to send SMS');
      }

      toast({
        title: "Text Sent!",
        description: `"${smsProblem.name}" texted to ${patientName}`,
      });

      setShowSmsModal(false);
      setSmsProblem(null);
      setSmsPatient(null);
      setSmsPatientSearchQuery('');
      
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send text. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingSMS(false);
    }
  };

  const handleEdit = async (problem) => {
    try {
      // Fetch BASE problem (without customizations) to show original template text
      const token = localStorage.getItem('dentalToken');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/problems-emergencies/library/${problem.id}/base`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const baseProblem = response.data.data;
        console.log('Loaded BASE problem for editing:', baseProblem.name, 'Overview length:', baseProblem.overview?.length || 0);
        
        // Set both the editing problem AND form data together
        setEditingProblem(baseProblem);
        setEditFormData({
          name: baseProblem.name || '',
          category: baseProblem.category || '',
          overview: baseProblem.overview || ''
        });
        
        console.log('Edit form opened with ORIGINAL template text');
      }
    } catch (error) {
      console.error('Failed to fetch problem details:', error);
      toast({
        title: "Error",
        description: "Failed to load problem details",
        variant: "destructive"
      });
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('dentalToken');
      
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/problems-emergencies/library/${editingProblem.id}`,
        editFormData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast({
        title: "Success",
        description: "Problem/Emergency updated successfully",
      });
      
      setEditingProblem(null);
      loadProblems();
    } catch (error) {
      console.error('Failed to update problem/emergency:', error);
      toast({
        title: "Error",
        description: "Failed to update problem/emergency",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (viewingProblem) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center flex-1">
                <Button 
                  variant="ghost" 
                  onClick={() => setViewingProblem(null)}
                  className="mr-4 p-2 hover:bg-gray-100 rounded-full"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex-1">
                  <Badge variant="secondary" className="mb-2">
                    {viewingProblem.category}
                  </Badge>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {viewingProblem.name}
                  </h1>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={() => handlePrint(viewingProblem)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Printer className="h-4 w-4" />
                  Print
                </Button>
                <Button 
                  onClick={() => handleDownload(viewingProblem)}
                  className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <Card className="mb-8 border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="text-gray-700 leading-relaxed">
                {(() => {
                  const rawLines = viewingProblem.overview.split('\n');
                  const elements = [];
                  let isFirstLines = true;
                  let i = 0;
                  
                  while (i < rawLines.length) {
                    const line = rawLines[i];
                    const trimmedLine = line.trim();
                    
                    // Skip empty lines
                    if (trimmedLine === '') {
                      elements.push(<div key={`empty-${i}`} className="mb-2"></div>);
                      i++;
                      continue;
                    }
                    
                    // First 2 non-empty lines are the title - larger and centered
                    if (isFirstLines && elements.length < 2 && !trimmedLine.startsWith('This handout')) {
                      elements.push(
                        <h2 key={`title-${i}`} className="font-bold text-gray-900 text-2xl text-center mb-2">
                          {trimmedLine}
                        </h2>
                      );
                      i++;
                      continue;
                    }
                    
                    isFirstLines = false;
                    
                    // Numbered items
                    if (trimmedLine.match(/^\d+\.\s+/)) {
                      elements.push(
                        <h4 key={`num-${i}`} className="font-bold text-gray-900 mt-4 mb-2">
                          {trimmedLine}
                        </h4>
                      );
                      i++;
                      continue;
                    }
                    
                    // Section headings
                    const isMainHeading = 
                      trimmedLine.match(/^Common Problems \(.*Emergency\)/i) ||
                      trimmedLine.match(/^When to Call the Dentist/i) ||
                      trimmedLine.match(/^Warning Signs & When to Call/i) ||
                      trimmedLine.match(/^What You May Notice/i);
                    
                    if (isMainHeading) {
                      elements.push(
                        <h3 key={`heading-${i}`} className="font-bold text-gray-900 mt-6 mb-3 text-lg">
                          {trimmedLine}
                        </h3>
                      );
                      i++;
                      continue;
                    }
                    
                    // Bullet points (indented)
                    if (line.startsWith(' ') || line.startsWith('\t')) {
                      const bulletText = trimmedLine.replace(/^[•-]\s*/, '');
                      elements.push(
                        <div key={`bullet-${i}`} className="flex items-start mb-1 ml-6">
                          <span className="text-gray-600 mr-2">•</span>
                          <span className="flex-1 text-gray-700">{bulletText}</span>
                        </div>
                      );
                      i++;
                      continue;
                    }
                    
                    // Regular paragraph - merge consecutive non-indented lines
                    let paragraph = trimmedLine;
                    let j = i + 1;
                    while (j < rawLines.length) {
                      const nextLine = rawLines[j];
                      const nextTrimmed = nextLine.trim();
                      
                      // Stop if empty, numbered, heading, or indented
                      if (nextTrimmed === '' || 
                          nextTrimmed.match(/^\d+\./) || 
                          nextLine.startsWith(' ') ||
                          nextLine.startsWith('\t') ||
                          nextTrimmed.match(/^(Common Problems|When to Call|Warning Signs|What You May)/i)) {
                        break;
                      }
                      
                      paragraph += ' ' + nextTrimmed;
                      j++;
                    }
                    
                    elements.push(
                      <p key={`para-${i}`} className="mb-2 text-gray-700 leading-relaxed">
                        {paragraph}
                      </p>
                    );
                    i = j;
                  }
                  
                  return elements;
                })()}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (editingProblem) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Edit Problem/Emergency</h2>
              <Button
                variant="outline"
                onClick={() => {
                  setEditingProblem(null);
                  setEditFormData({ name: '', category: '', overview: '' });
                }}
              >
                Cancel
              </Button>
            </div>

            <Card>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      placeholder="e.g., Bleeding After Extraction"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={editFormData.category}
                      onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Category</option>
                      {categories.filter(c => c !== 'All').map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Overview */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Overview/Instructions <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={editFormData.overview}
                      onChange={(e) => setEditFormData({ ...editFormData, overview: e.target.value })}
                      rows={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder="Enter the problem/emergency instructions..."
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      Character count: {editFormData.overview?.length || 0} | Tip: Use blank lines to separate paragraphs, indent lines for bullet points
                    </p>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingProblem(null);
                        setEditFormData({ name: '', category: '', overview: '' });
                      }}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveEdit}
                      disabled={saving || !editFormData.name || !editFormData.category || !editFormData.overview}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="mr-4"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Problems & Emergencies Library</h1>
                <p className="text-gray-600 mt-1">Quick reference guides for common dental issues</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>Important:</strong> These are template documents. You must customize them for your practice and have them reviewed by your legal counsel before use.
          </AlertDescription>
        </Alert>
      </div>

      {/* Search and Filter */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    placeholder="Search problems and emergencies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {categories.map(category => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Problems Grid */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProblems.map(problem => (
              <Card key={problem.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{problem.name}</CardTitle>
                      <Badge variant="secondary" className="mt-2">
                        {problem.category}
                      </Badge>
                    </div>
                    <AlertCircle className="h-8 w-8 text-red-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleView(problem)}
                      className="flex items-center justify-center gap-1"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(problem)}
                      className="flex items-center justify-center gap-1"
                    >
                      <Edit2 className="h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTextProblem(problem)}
                      className="flex items-center justify-center gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                    >
                      <MessageSquare className="h-4 w-4" />
                      Text
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePrint(problem)}
                      className="flex items-center justify-center gap-1"
                    >
                      <Printer className="h-4 w-4" />
                      Print
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(problem)}
                      className="flex items-center justify-center gap-1"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && filteredProblems.length === 0 && (
          <div className="text-center py-12">
            <AlertCircle className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No problems/emergencies found</h3>
            <p className="text-gray-600">
              {searchTerm || selectedCategory !== 'All' 
                ? 'Try adjusting your search or filters'
                : 'No items available yet'
              }
            </p>
          </div>
        )}
      </div>

      {/* SMS Modal */}
      <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-600" />
              Text to Patient
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a patient to text <span className="font-medium text-orange-600">{smsProblem?.name}</span>
            </p>
            
            {/* Patient Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by name or email..."
                value={smsPatientSearchQuery}
                onChange={(e) => setSmsPatientSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Patient List */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredSmsPatients.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Phone className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium">
                    {smsPatientSearchQuery 
                      ? 'No patients with mobile numbers match your search' 
                      : 'No patients with mobile numbers available'}
                  </p>
                  <p className="text-xs mt-1">Only patients with phone numbers can receive text messages</p>
                </div>
              ) : (
                filteredSmsPatients.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() => setSmsPatient(patient)}
                    className={`p-3 border rounded-lg transition cursor-pointer ${
                      smsPatient?.id === patient.id
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-xs text-gray-600 flex items-center">
                          <Phone className="h-3 w-3 mr-1" />
                          {patient.cellPhone || patient.phone}
                        </p>
                      </div>
                      {smsPatient?.id === patient.id && (
                        <Check className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSmsModal(false);
                  setSmsProblem(null);
                  setSmsPatient(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={sendSMS}
                disabled={!smsPatient || sendingSMS}
                className="bg-green-600 hover:bg-green-700"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                {sendingSMS ? 'Sending...' : 'Send Text'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProblemsEmergenciesLibraryPage;
